import {
  createRootRoute,
  createRoute,
  createRouter,
  lazyRouteComponent,
  Outlet,
  redirect,
  useRouterState,
} from '@tanstack/react-router';
import { PlatformProvider } from './components/workbench/PlatformContext';
import { WorkbenchShell } from './components/workbench/WorkbenchShell';
import { NotFoundPage, RouteErrorPage } from './components/RouterErrorPage';
import { resolveWorkbenchView } from './config/workbench';
import { readStoredUser, tokenKey } from './lib/auth';

function Root() {
  const location = useRouterState({ select: (state) => state.location });
  const storedUser = readStoredUser();
  const authenticated = Boolean(location.href && localStorage.getItem(tokenKey) && storedUser);
  const workbenchView = resolveWorkbenchView(location.pathname);

  if (!authenticated || !storedUser || !workbenchView) return <Outlet />;

  const userScope = storedUser.id ?? storedUser.email;
  return (
    <PlatformProvider key={userScope} userScope={userScope}>
      <WorkbenchShell view={workbenchView}>
        <Outlet />
      </WorkbenchShell>
    </PlatformProvider>
  );
}

const rootRoute = createRootRoute({ component: Root });

function requireAuth({ location }: { location: { href: string } }) {
  if (!localStorage.getItem(tokenKey)) {
    throw redirect({
      to: '/login',
      search: { return_to: location.href },
    });
  }
}

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  beforeLoad: requireAuth,
  component: lazyRouteComponent(() => import('./pages/OverviewPage'), 'OverviewPage'),
});
const pluginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/plugin',
  beforeLoad: requireAuth,
  component: lazyRouteComponent(() => import('./pages/PluginPage'), 'PluginPage'),
});
const accountDataRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/account-data',
  beforeLoad: requireAuth,
  component: lazyRouteComponent(() => import('./pages/AccountDataPage'), 'AccountDataPage'),
});
const skillsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/skills',
  beforeLoad: requireAuth,
  component: lazyRouteComponent(() => import('./pages/SkillsPage'), 'SkillsPage'),
});
const diagnosisRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/diagnosis',
  beforeLoad: requireAuth,
  component: lazyRouteComponent(() => import('./pages/DiagnosisPage'), 'DiagnosisPage'),
});
const billingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/billing',
  beforeLoad: requireAuth,
  component: lazyRouteComponent(() => import('./pages/BillingPage'), 'BillingPage'),
});
const competitorRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/competitor',
  beforeLoad: requireAuth,
  component: lazyRouteComponent(() => import('./pages/CompetitorPage'), 'CompetitorPage'),
});
const savedTopicsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/saved-topics',
  beforeLoad: requireAuth,
  component: lazyRouteComponent(() => import('./pages/SavedTopicsPage'), 'SavedTopicsPage'),
});
const creatorRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/creator',
  beforeLoad: requireAuth,
  component: lazyRouteComponent(() => import('./pages/CreatorPage'), 'CreatorPage'),
});
const creatorDraftsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/creator/drafts',
  beforeLoad: requireAuth,
  component: lazyRouteComponent(() => import('./pages/CreatorDraftsPage'), 'CreatorDraftsPage'),
});
const helpRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/help',
  beforeLoad: requireAuth,
  component: lazyRouteComponent(() => import('./pages/HelpPage'), 'HelpPage'),
});
const settingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settings',
  beforeLoad: requireAuth,
  component: lazyRouteComponent(() => import('./pages/SettingsPage'), 'SettingsPage'),
});
const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: lazyRouteComponent(() => import('./pages/LoginPage'), 'LoginPage'),
});
const authCallbackRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/auth/callback',
  component: lazyRouteComponent(() => import('./pages/AuthCallbackPage'), 'AuthCallbackPage'),
});
const extensionConnectRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/auth/extension-connect',
  component: lazyRouteComponent(
    () => import('./pages/ExtensionConnectPage'),
    'ExtensionConnectPage',
  ),
});

export const router = createRouter({
  defaultErrorComponent: RouteErrorPage,
  defaultNotFoundComponent: NotFoundPage,
  defaultPreload: 'intent',
  routeTree: rootRoute.addChildren([
    indexRoute,
    pluginRoute,
    accountDataRoute,
    skillsRoute,
    diagnosisRoute,
    billingRoute,
    competitorRoute,
    savedTopicsRoute,
    creatorRoute,
    creatorDraftsRoute,
    helpRoute,
    settingsRoute,
    loginRoute,
    authCallbackRoute,
    extensionConnectRoute,
  ]),
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
